import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { formatDate } from "../utils/i18nFormat";
import { getEdgeIncidents, getEdgeIncident, labelEdgeIncident, rediagnoseEdgeIncident } from "../api/edgeApi";
import {
  alarmFieldLabel,
  DIAGNOSIS_STATUS_LABELS,
  FAILURE_DOMAIN_LABELS,
} from "../utils/alarmLabels";

// "Olaylar" sekmesi: edge'in bir koruma mekanizması devreye girdiğinde dondurduğu kanıt özeti ve
// (anahtar girilmişse) danışman teşhis. Teşhis yalnızca tavsiyedir, hiçbir şey otomatik uygulanmaz.

const box = {
  border: "1px solid var(--border)",
  borderRadius: "8px",
  padding: "10px 14px",
  backgroundColor: "var(--bg-hover)",
};
const btn = {
  background: "transparent",
  border: "1px solid var(--border)",
  borderRadius: "6px",
  color: "var(--text-primary)",
  padding: "4px 10px",
  cursor: "pointer",
  fontSize: "12px",
};
const input = {
  width: "100%",
  boxSizing: "border-box",
  background: "var(--bg-primary, transparent)",
  border: "1px solid var(--border)",
  borderRadius: "6px",
  color: "var(--text-primary)",
  padding: "6px 8px",
  fontSize: "13px",
};

function Diagnosis({ d, title }) {
  const { t } = useTranslation();
  if (!d) return null;
  return (
    <div style={{ marginTop: "8px" }}>
      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>{title}</div>
      <div style={{ fontWeight: 600 }}>
        {FAILURE_DOMAIN_LABELS[d.failure_domain] ?? d.failure_domain}
        {d.escalate && (
          <span style={{ marginLeft: "8px", color: "var(--danger)", fontSize: "12px" }}>{t("edgeDetail.incidents.escalate")}</span>
        )}
      </div>
      {d.summary && <div style={{ margin: "4px 0", color: "var(--text-secondary)" }}>{d.summary}</div>}
      {d.hypotheses?.length > 0 && (
        <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: "13px" }}>
          {d.hypotheses.map((h) => (
            <li key={h.name}>
              {h.name} <span style={{ color: "var(--text-muted)" }}>({Math.round(h.confidence * 100)}%)</span>
            </li>
          ))}
        </ul>
      )}
      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>
        {t("edgeDetail.incidents.nextStep", { action: d.recommended_action, probe: d.requested_probe ?? t("edgeDetail.incidents.none") })}
      </div>
    </div>
  );
}

function IncidentDetail({ accessToken, edgeDbId, row, canLabel, onLabeled }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [cause, setCause] = useState("");
  const [fix, setFix] = useState("");
  const [saving, setSaving] = useState(false);
  const [requeueing, setRequeueing] = useState(false);

  useEffect(() => {
    let alive = true;
    getEdgeIncident(accessToken, edgeDbId, row.id)
      .then((d) => alive && setDetail(d))
      .catch(() => alive && setDetail({ error: true }));
    return () => {
      alive = false;
    };
  }, [accessToken, edgeDbId, row.id]);

  if (!detail) return <div style={{ padding: "8px", color: "var(--text-muted)" }}>{t("edgeDetail.incidents.loading")}</div>;
  if (detail.error) return <div style={{ padding: "8px", color: "var(--danger)" }}>{t("edgeDetail.incidents.detailFailed")}</div>;

  const save = async () => {
    setSaving(true);
    try {
      await labelEdgeIncident(accessToken, edgeDbId, row.id, cause, fix);
      toast.success(t("edgeDetail.incidents.labelSaved"));
      onLabeled();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || t("edgeDetail.incidents.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const p = detail.payload || {};
  return (
    <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "10px" }}>
      {detail.diagnosis_status !== "done" && (
        <div style={{ color: "var(--text-muted)", fontSize: "13px" }}>
          {DIAGNOSIS_STATUS_LABELS[detail.diagnosis_status] ?? detail.diagnosis_status}
          {detail.diagnosis_error ? ` — ${detail.diagnosis_error}` : ""}
        </div>
      )}
      {detail.diagnosis_status !== "pending" && (
        <div>
          <button
            style={btn}
            disabled={requeueing}
            onClick={async () => {
              setRequeueing(true);
              try {
                await rediagnoseEdgeIncident(accessToken, edgeDbId, row.id);
                toast.success(t("edgeDetail.incidents.requeued"));
                onLabeled();
              } catch (err) {
                toast.error(err?.response?.data?.detail || t("edgeDetail.incidents.queueFailed"));
              } finally {
                setRequeueing(false);
              }
            }}
          >
            {t("edgeDetail.incidents.rediagnose")}
          </button>
        </div>
      )}
      <Diagnosis d={detail.diagnosis} title={t("edgeDetail.incidents.advisorTitle", { model: detail.model ?? "-" })} />
      {detail.probe && (
        <div style={box}>
          <div style={{ fontWeight: 600 }}>
            {t("edgeDetail.incidents.probe")} {detail.probe.probe}{" "}
            <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>({detail.probe.status})</span>
          </div>
          {detail.probe.result && (
            <pre style={{ margin: "6px 0 0", maxHeight: "200px", overflow: "auto", fontSize: "12px", whiteSpace: "pre-wrap" }}>
              {detail.probe.result}
            </pre>
          )}
          <Diagnosis d={detail.probe.followup_diagnosis} title={t("edgeDetail.incidents.followup")} />
        </div>
      )}

      <details>
        <summary style={{ cursor: "pointer" }}>{t("edgeDetail.incidents.evidence")}</summary>
        <div style={{ fontSize: "12px", marginTop: "6px" }}>
          <div>{t("edgeDetail.incidents.uptimeOs", { uptime: p.uptime_sec ?? "-", os: p.os || "-" })}</div>
          {p.containers?.length > 0 && <pre style={{ whiteSpace: "pre-wrap" }}>{p.containers.join("\n")}</pre>}
          {p.journal_tail?.length > 0 && (
            <pre style={{ whiteSpace: "pre-wrap", maxHeight: "220px", overflow: "auto" }}>{p.journal_tail.join("\n")}</pre>
          )}
          {p.dmesg_tail?.length > 0 && (
            <pre style={{ whiteSpace: "pre-wrap", maxHeight: "160px", overflow: "auto" }}>{p.dmesg_tail.join("\n")}</pre>
          )}
        </div>
      </details>

      <div style={box}>
        <div style={{ fontWeight: 600, marginBottom: "6px" }}>{t("edgeDetail.incidents.rootCause")}</div>
        {detail.root_cause ? (
          <div>
            {detail.root_cause}
            {detail.fix_applied && <div style={{ color: "var(--text-secondary)" }}>{t("edgeDetail.incidents.fixApplied", { fix: detail.fix_applied })}</div>}
          </div>
        ) : canLabel ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <input style={input} placeholder={t("edgeDetail.incidents.causePlaceholder")}
              value={cause} onChange={(e) => setCause(e.target.value)} maxLength={300} />
            <input style={input} placeholder={t("edgeDetail.incidents.fixPlaceholder")} value={fix}
              onChange={(e) => setFix(e.target.value)} maxLength={300} />
            <div>
              <button style={btn} disabled={saving || cause.trim().length < 3} onClick={save}>
                {t("edgeDetail.incidents.save")}
              </button>
              <span style={{ marginLeft: "8px", fontSize: "12px", color: "var(--text-muted)" }}>
                {t("edgeDetail.incidents.saveHint")}
              </span>
            </div>
          </div>
        ) : (
          <div style={{ color: "var(--text-muted)" }}>{t("edgeDetail.incidents.notLabeled")}</div>
        )}
      </div>
    </div>
  );
}

export default function EdgeIncidentsTab({ accessToken, edgeDbId, canLabel }) {
  const { t } = useTranslation();
  // Details, diagnosis and labelling are the superadmin's. Operators and tenant admins get a read-only summary list (the server
  // enforces the same: the list carries no diagnosis for them, detail and label answer 403).
  const superadmin = useSelector((s) => s.session?.me?.global_role) === "superadmin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken || !edgeDbId) return;
    setLoading(true);
    try {
      const data = await getEdgeIncidents(accessToken, edgeDbId);
      setRows(Array.isArray(data) ? data : []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, edgeDbId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div style={{ padding: "32px", color: "var(--text-muted)", textAlign: "center" }}>{t("edgeDetail.incidents.loading")}</div>;

  return (
    <div style={{ color: "var(--text-primary)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <h3 style={{ margin: 0, fontSize: "16px" }}>{t("edgeDetail.incidents.title")}</h3>
        <div style={{ display: "flex", gap: "8px" }}>
          {superadmin && rows.some((r) => ["failed", "rate_limited", "skipped_no_key"].includes(r.diagnosis_status)) && (
            <button
              style={btn}
              onClick={async () => {
                try {
                  const res = await rediagnoseEdgeIncident(accessToken, edgeDbId);
                  toast.success(t("edgeDetail.incidents.queuedCount", { count: res.queued }));
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || t("edgeDetail.incidents.queueFailed"));
                }
              }}
            >
              {t("edgeDetail.incidents.retryFailed")}
            </button>
          )}
          <button style={btn} onClick={load}>{t("edgeDetail.common.refresh")}</button>
        </div>
      </div>
      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "12px" }}>
        {superadmin
          ? t("edgeDetail.incidents.introAdmin")
          : t("edgeDetail.incidents.introReadonly")}
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: "32px", textAlign: "center", color: "var(--text-muted)" }}>{t("edgeDetail.incidents.empty")}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {rows.map((r) => {
            const open = superadmin && openId === r.id;
            return (
              <div key={r.id} style={box}>
                <div style={{ display: "flex", gap: "14px", alignItems: "center", cursor: superadmin ? "pointer" : "default" }}
                  onClick={superadmin ? () => setOpenId(open ? null : r.id) : undefined}>
                  <span style={{ fontSize: "12px", color: "var(--text-muted)", minWidth: "140px" }}>
                    {r.received_at ? formatDate(new Date(r.received_at + "Z"), { dateStyle: "short", timeStyle: "medium" }) : "-"}
                  </span>
                  <span style={{ fontWeight: 600, minWidth: "200px" }} title={r.trigger}>
                    {alarmFieldLabel(r.trigger)}
                  </span>
                  <span style={{ flex: 1, color: "var(--text-secondary)", fontSize: "13px" }}>
                    {superadmin ? (r.diagnosis?.summary ?? DIAGNOSIS_STATUS_LABELS[r.diagnosis_status] ?? r.diagnosis_status) : ""}
                  </span>
                  {superadmin && r.root_cause && <span style={{ fontSize: "12px", color: "var(--success)" }}>{t("edgeDetail.incidents.labelled")}</span>}
                  {superadmin && <span>{open ? "▾" : "▸"}</span>}
                </div>
                {open && (
                  <IncidentDetail accessToken={accessToken} edgeDbId={edgeDbId} row={r} canLabel={superadmin && canLabel}
                    onLabeled={load} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
